import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { EllipsisVertical } from 'lucide-react';
import { createPortal } from 'react-dom';
import './OptionsMenu.css';

const VIEWPORT_PAD = 8;
const MENU_GAP = 8;
const MENU_WIDTH_ESTIMATE = 160;
const MENU_ROW_HEIGHT = 36;

function clampMenuPosition(buttonRect, menuWidth, menuHeight) {
  let top = buttonRect.bottom + MENU_GAP;
  let left = buttonRect.right - menuWidth;

  if (left < VIEWPORT_PAD) {
    left = VIEWPORT_PAD;
  }
  if (left + menuWidth > window.innerWidth - VIEWPORT_PAD) {
    left = Math.max(VIEWPORT_PAD, window.innerWidth - menuWidth - VIEWPORT_PAD);
  }

  if (top + menuHeight > window.innerHeight - VIEWPORT_PAD) {
    top = buttonRect.top - menuHeight - MENU_GAP;
  }
  if (top < VIEWPORT_PAD) {
    top = VIEWPORT_PAD;
  }

  return { top, left };
}

const OptionsMenu = ({ options, buttonClassName = 'options-button' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth || MENU_WIDTH_ESTIMATE;
    const menuHeight =
      menuRef.current?.offsetHeight || options.length * MENU_ROW_HEIGHT + 16;
    setMenuPosition(clampMenuPosition(buttonRect, menuWidth, menuHeight));
  }, [options.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onViewportChange = () => updateMenuPosition();
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);

    return () => {
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [isOpen, updateMenuPosition]);

  const handleButtonClick = (e) => {
    e.stopPropagation();

    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const estimatedHeight = options.length * MENU_ROW_HEIGHT + 16;
      setMenuPosition(clampMenuPosition(rect, MENU_WIDTH_ESTIMATE, estimatedHeight));
    }

    setIsOpen((open) => !open);
  };

  const handleOptionClick = (e, onClick) => {
    e.stopPropagation();
    onClick(e);
    setIsOpen(false);
  };

  return (
    <div className="options-menu-container">
      <button
        ref={buttonRef}
        type="button"
        className={buttonClassName}
        onClick={handleButtonClick}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <EllipsisVertical size={20} aria-hidden="true" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="options-popup-menu"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}
            role="menu"
          >
            {options.map((option, index) => (
              <button
                key={index}
                type="button"
                role="menuitem"
                className={`menu-option ${option.className || ''}`}
                onClick={(e) => handleOptionClick(e, option.onClick)}
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default OptionsMenu;
